import { getFunctions } from 'firebase/functions';
import { useMemo, type ReactNode } from 'react';

import { createFirebaseAccountLinkAdapter } from '../../infrastructure/firebase/firebaseAccountLinkAdapter';
import { createFirebaseAuthAdapter } from '../../infrastructure/firebase/firebaseAuthAdapter';
import { createFirebaseDependentAdapter } from '../../infrastructure/firebase/firebaseDependentAdapter';
import { createFirebaseEmployeeAdapter } from '../../infrastructure/firebase/firebaseEmployeeAdapter';
import { createFirebaseFundPolicyAdapter } from '../../infrastructure/firebase/firebaseFundPolicyAdapter';
import { createFirebaseDeclarationAdapter } from '../../infrastructure/firebase/firebaseDeclarationAdapter';
import { createFirebaseDocumentAdapter } from '../../infrastructure/firebase/firebaseDocumentAdapter';
import { createFirebaseProgressAdapter } from '../../infrastructure/firebase/firebaseProgressAdapter';
import { createFirebaseEmployeeAdminAdapter } from '../../infrastructure/firebase/firebaseEmployeeAdminAdapter';
import { createFirebaseRegistrationReviewAdapter } from '../../infrastructure/firebase/firebaseRegistrationReviewAdapter';
import { createFirebaseRolesAdapter } from '../../infrastructure/firebase/firebaseRolesAdapter';
import { createFirebaseSettingsAdapter } from '../../infrastructure/firebase/firebaseSettingsAdapter';
import { createFirebaseOrganisationAdapter } from '../../infrastructure/firebase/firebaseOrganisationAdapter';
import { initialiseFirebase } from '../../infrastructure/firebase/firebaseClient';
import { loadAppConfig } from '../config/firebaseConfig';

import { ServicesContext, type Services } from './servicesContext';

/**
 * Composition root wiring (blueprint §3.1, §5.5).
 *
 * `app/` is the only layer that may reach both the ports and their
 * implementations. Everything below receives the ports and never learns which
 * platform is behind them — which is what lets a screen be tested with a stub
 * and no network.
 */
export function AppProviders({
  children,
  overrides,
}: {
  readonly children: ReactNode;
  /** Test seam: supply stubs instead of touching Firebase. */
  readonly overrides?: Partial<Services>;
}) {
  const services = useMemo<Services>(() => {
    // Fully stubbed: skip Firebase entirely, so a test never needs config.
    // Fully stubbed only when every port is supplied; a partial stub would
    // reach for Firebase config a test does not have.
    if (
      overrides?.auth &&
      overrides.links &&
      overrides.organisation &&
      overrides.employees &&
      overrides.dependents &&
      overrides.fundPolicy &&
      overrides.declarations &&
      overrides.documents &&
      overrides.progress &&
      overrides.settings &&
      overrides.registrationReview &&
      overrides.employeeAdmin &&
      overrides.roles
    ) {
      return {
        auth: overrides.auth,
        links: overrides.links,
        organisation: overrides.organisation,
        employees: overrides.employees,
        dependents: overrides.dependents,
        fundPolicy: overrides.fundPolicy,
        declarations: overrides.declarations,
        documents: overrides.documents,
        progress: overrides.progress,
        settings: overrides.settings,
        registrationReview: overrides.registrationReview,
        employeeAdmin: overrides.employeeAdmin,
        roles: overrides.roles,
      };
    }

    const config = loadAppConfig(import.meta.env);
    const firebase = initialiseFirebase(config);
    const functions = getFunctions(firebase.app, 'europe-west1');

    return {
      auth: overrides?.auth ?? createFirebaseAuthAdapter(firebase.auth),
      links:
        overrides?.links ??
        createFirebaseAccountLinkAdapter(functions, firebase.firestore, firebase.auth),
      organisation:
        overrides?.organisation ?? createFirebaseOrganisationAdapter(firebase.firestore),
      employees:
        overrides?.employees ??
        createFirebaseEmployeeAdapter(firebase.firestore, firebase.auth),
      dependents:
        overrides?.dependents ??
        createFirebaseDependentAdapter(firebase.firestore, firebase.auth),
      fundPolicy:
        overrides?.fundPolicy ?? createFirebaseFundPolicyAdapter(firebase.firestore),
      declarations:
        overrides?.declarations ??
        createFirebaseDeclarationAdapter(firebase.firestore, firebase.auth),
      documents:
        overrides?.documents ??
        createFirebaseDocumentAdapter(firebase.firestore, firebase.storage, firebase.auth),
      progress: overrides?.progress ?? createFirebaseProgressAdapter(firebase.firestore),
      settings:
        overrides?.settings ??
        createFirebaseSettingsAdapter(firebase.firestore, firebase.auth),
      registrationReview:
        overrides?.registrationReview ??
        createFirebaseRegistrationReviewAdapter(firebase.firestore, functions),
      employeeAdmin:
        overrides?.employeeAdmin ??
        createFirebaseEmployeeAdminAdapter(firebase.firestore, firebase.auth),
      roles:
        overrides?.roles ?? createFirebaseRolesAdapter(firebase.firestore, functions),
    };
  }, [overrides]);

  return <ServicesContext value={services}>{children}</ServicesContext>;
}
